/**
 * A question's options, keyed by letter: `{ A: "…", B: "…" }`.
 *
 * Extracted questions don't always start at A: the PQX engine can drop an
 * option it couldn't read, leaving `{ B: "…" }` or `{ A, B, D }`. Adding and
 * listing go by the letters themselves, not by how many there are.
 */
export type Options = Record<string, string>;

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** The letters in alphabetical order, whatever order the object holds them in. */
export function optionLetters(options: Options): string[] {
  return Object.keys(options).sort();
}

/** The options rebuilt in alphabetical order, so they display A, B, C… */
export function sortedOptions(options: Options): Options {
  return Object.fromEntries(optionLetters(options).map((k) => [k, options[k] ?? ""]));
}

/**
 * The letter just before the first option, or null when the first is already
 * A — there is nothing to add above it.
 */
export function letterBefore(options: Options): string | null {
  const first = optionLetters(options)[0];
  if (!first) return "A";
  const i = LETTERS.indexOf(first);
  return i > 0 ? LETTERS[i - 1]! : null;
}

/**
 * The letter just after the last option, or null past Z. An empty set starts
 * at A. The next free letter after the last, so a gap (A, B, D) adds E, never
 * a duplicate of a letter already there.
 */
export function letterAfter(options: Options): string | null {
  const letters = optionLetters(options);
  const last = letters[letters.length - 1];
  if (!last) return "A";
  const i = LETTERS.indexOf(last);
  return i >= 0 && i < LETTERS.length - 1 ? LETTERS[i + 1]! : null;
}

/** With a new, empty option at `letter`, in alphabetical order. A letter already there is left alone. */
export function withOption(options: Options, letter: string): Options {
  if (letter in options) return sortedOptions(options);
  return sortedOptions({ ...options, [letter]: "" });
}
